import { useCallback, useRef } from 'preact/hooks'
import { ElementData, FormFlags, FormData } from '../../types'
import { useWidgetContext } from '../useWidgetContext'
import { useAppDispatch, useAppSelector } from '../../store/store'
import { selectWidgetById, setFormFieldValue } from '../../store/widgets'
import { submitForm } from '../../store/submit'

const SUBMITTER_TYPE_REGEXP = /^(?:submit|button|image|reset|file)$/i
const SUBMITTABLE_REGEXP = /^(?:input|select|textarea)/i
const CHECKABLE_TYPE_REGEXP = /^(?:checkbox|radio)$/i

type SubmittableElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

function isSubmittable(el: Element): el is SubmittableElement {
  const { type, nodeName, name } = el as any
  return !!name && SUBMITTABLE_REGEXP.test(nodeName) && !SUBMITTER_TYPE_REGEXP.test(type)
}

function getElementValue(el: SubmittableElement): string | null {
  if ((el as HTMLInputElement).checked || !CHECKABLE_TYPE_REGEXP.test(el.type)) {
    return el.value
  }
  return null
}

function getFormData(form: HTMLFormElement): FormData {
  const elements = Array.from(form.elements).filter(isSubmittable)
  return elements.reduce<FormData>((res, el) => {
    const { name, validity } = el
    const value = getElementValue(el)
    const curr = res[name] || { value: [], valid: true }
    res[name] = {
      value: [...curr.value, ...(value != '' && value != null ? [value] : [])],
      valid: curr.valid && validity.valid,
    }
    return res
  }, {})
}

export interface UseForm {
  handleSubmit(onSubmit?: () => void): (e: Event) => void

  setValue(name: string, value: ElementData): void

  getValue(name: string): string

  getMultiValue(name: string): string[]

  getValidity(name: string): boolean

  getFormFlags(): FormFlags
}

export const useForm = (): UseForm => {
  const { widgetId } = useWidgetContext()
  const formState = useAppSelector(state => selectWidgetById(state, widgetId).formState)
  const dispatch = useAppDispatch()

  const stateRef = useRef(formState)
  stateRef.current = formState

  const handleSubmit = useCallback(
    (onSubmit?: () => void) => async (e: Event) => {
      e.preventDefault()
      const form: HTMLFormElement = e.target as HTMLFormElement
      const valid = form.checkValidity()
      const data = getFormData(form)

      await dispatch(submitForm(widgetId, { data, valid }))
      onSubmit?.()
    },
    [dispatch, widgetId],
  )

  const setValue = useCallback(
    (name: string, value: ElementData) => {
      dispatch(setFormFieldValue(widgetId, name, value))
    },
    [dispatch, widgetId],
  )

  const getValue = useCallback((name: string) => {
    const v = stateRef.current.data[name]
    return v?.value?.[0] || ''
  }, [])

  const getMultiValue = useCallback((name: string) => {
    const v = stateRef.current.data[name]
    return v?.value || []
  }, [])

  const getValidity = useCallback((name: string) => {
    const { valid = true } = stateRef.current.data[name] || {}
    return valid
  }, [])

  const getFormFlags = useCallback(() => {
    const { data, ...flags } = stateRef.current
    return flags
  }, [])

  return {
    handleSubmit,
    setValue,
    getValue,
    getMultiValue,
    getValidity,
    getFormFlags,
  }
}
